import { useState, useEffect } from 'react';
import api, { urlDocument } from 'services/api';
import { apercuDocument } from 'services/documents';
import BoutonTelechargerDocument from 'components/shared/BoutonTelechargerDocument';
import ImageProtegee from 'components/ui/ImageProtegee';

const EMPTY_MODULE = { titre: '', description: '', contenu: '', duree_estimee: '', type: 'theorique', ordre: 0, obligatoire: true, parcours_id: '' };

export default function Formations() {
  const [tab, setTab] = useState('dashboard');
  const [parcours, setParcours] = useState([]);
  const [modules, setModules] = useState([]);
  const [dashboard, setDashboard] = useState([]);
  const [attestations, setAttestations] = useState([]);
  const [showParcoursForm, setShowParcoursForm] = useState(false);
  const [parcoursForm, setParcoursForm] = useState({ code_fonction: '', titre: '', description: '' });
  const [editParcoursId, setEditParcoursId] = useState(null);
  const [showModuleForm, setShowModuleForm] = useState(false);
  const [moduleForm, setModuleForm] = useState(EMPTY_MODULE);
  const [editModuleId, setEditModuleId] = useState(null);
  const [docFile, setDocFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(null);
  const [filterParcours, setFilterParcours] = useState('');

  const loadAll = async (parcoursId) => {
    setLoading(true);
    try {
      const dashUrl = parcoursId ? `/formations/dashboard?parcours_id=${parcoursId}` : '/formations/dashboard';
      const [parc, mods, dash, atts] = await Promise.all([
        api.get('/formations/parcours'),
        api.get('/formations/modules'),
        api.get(dashUrl),
        api.get('/formations/attestations'),
      ]);
      setParcours(parc.data);
      setModules(mods.data);
      setDashboard(dash.data);
      setAttestations(atts.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { loadAll(filterParcours); }, [filterParcours]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleModuleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(moduleForm).forEach(([k, v]) => formData.append(k, v));
      if (docFile) formData.append('document', docFile);

      if (editModuleId) {
        await api.put(`/formations/modules/${editModuleId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post('/formations/modules', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setShowModuleForm(false);
      setModuleForm(EMPTY_MODULE);
      setEditModuleId(null);
      setDocFile(null);
      loadAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleEditModule = (mod) => {
    setModuleForm({
      titre: mod.titre, description: mod.description || '', contenu: mod.contenu || '',
      duree_estimee: mod.duree_estimee || '', type: mod.type, ordre: mod.ordre, obligatoire: mod.obligatoire,
      parcours_id: mod.parcours_id || '',
    });
    setEditModuleId(mod.id);
    setShowModuleForm(true);
  };

  const handleParcoursSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editParcoursId) {
        await api.put(`/formations/parcours/${editParcoursId}`, parcoursForm);
      } else {
        await api.post('/formations/parcours', parcoursForm);
      }
      setShowParcoursForm(false);
      setParcoursForm({ code_fonction: '', titre: '', description: '' });
      setEditParcoursId(null);
      loadAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleEditParcours = (p) => {
    setParcoursForm({ code_fonction: p.code_fonction, titre: p.titre, description: p.description || '' });
    setEditParcoursId(p.id);
    setShowParcoursForm(true);
  };

  const handleGenererAttestation = async (agentId) => {
    setGenerating(agentId);
    try {
      const res = await api.post(`/formations/attestation/${agentId}`);
      await apercuDocument(urlDocument('attestation', res.data.id));
      loadAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Impossible de generer l\'attestation');
      if (err.response?.data?.modules_manquants) {
        setError(`Modules non valides : ${err.response.data.modules_manquants.join(', ')}`);
      }
    } finally { setGenerating(null); }
  };

  const setMField = (k, v) => setModuleForm(f => ({ ...f, [k]: v }));

  const complets = dashboard.filter(a => a.total_modules > 0 && a.modules_valides === a.total_modules).length;
  const enCours = dashboard.filter(a => a.modules_valides > 0 && a.modules_valides < a.total_modules).length;


  return (
    <div>
<div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Modules</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg></div></div>
          <div className="stat-card-value">{modules.filter(m => m.actif).length}</div>
          <div className="stat-card-sub">modules actifs</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Formation complete</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg></div></div>
          <div className="stat-card-value" style={{ color: 'var(--gss-success)' }}>{complets}</div>
          <div className="stat-card-sub">agents certifies</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">En cours</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></div></div>
          <div className="stat-card-value" style={{ color: 'var(--gss-warning)' }}>{enCours}</div>
          <div className="stat-card-sub">agents en formation</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Attestations</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></div></div>
          <div className="stat-card-value">{attestations.length}</div>
          <div className="stat-card-sub">delivrees</div>
        </div>
      </div>

      {error && <div className="gss-alert gss-alert-error">{error}<button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>&times;</button></div>}

      {/* Onglets */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {[
          { id: 'dashboard', label: 'Suivi agents' },
          { id: 'parcours', label: 'Parcours' },
          { id: 'modules', label: 'Modules' },
          { id: 'attestations', label: 'Attestations' },
        ].map(t => (
          <button key={t.id} className={`gss-btn ${tab === t.id ? 'gss-btn-primary' : ''}`} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p>
      ) : (
        <>
          {/* ── DASHBOARD SUIVI ── */}
          {tab === 'dashboard' && (
            <div className="content-card">
              <div className="gss-toolbar" style={{ marginBottom: '1rem' }}>
                <div className="content-card-title" style={{ marginBottom: 0 }}>Progression des agents</div>
                <select
                  className="gss-input"
                  style={{ width: 'auto', minWidth: 200 }}
                  value={filterParcours}
                  onChange={e => setFilterParcours(e.target.value)}
                >
                  <option value="">Tous les parcours</option>
                  {parcours.filter(p => p.actif).map(p => (
                    <option key={p.id} value={p.id}>{p.titre}</option>
                  ))}
                </select>
              </div>
              {dashboard.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--gss-text-light)" strokeWidth="1.5" style={{ marginBottom: 16 }}>
                    <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/>
                  </svg>
                  <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--gss-text-secondary)', marginBottom: 4 }}>Aucune formation enregistree</div>
                  <div style={{ fontSize: 14, color: 'var(--gss-text-light)' }}>Cliquez sur le bouton pour commencer</div>
                </div>
              ) : (
                <div className="gss-table-wrap">
                  <table className="gss-table">
                    <thead>
                      <tr><th></th><th>Matricule</th><th>Personne</th><th>Type</th><th>Progression</th><th>Statut</th><th>Action</th></tr>
                    </thead>
                    <tbody>
                      {dashboard.map(a => {
                        const pct = a.total_modules > 0 ? Math.round((a.modules_valides / a.total_modules) * 100) : 0;
                        const complet = a.total_modules > 0 && a.modules_valides === a.total_modules;
                        const attenteSup = a.modules_assignes > 0 && a.modules_valides_sup < a.total_modules && !complet;
                        const attenteRH = a.modules_valides_sup === a.total_modules && a.modules_valides < a.total_modules && a.total_modules > 0;

                        let statut, statutBadge, barColor;
                        if (complet) {
                          statut = 'Complet';
                          statutBadge = 'gss-badge gss-badge-green';
                          barColor = 'var(--gss-success)';
                        } else if (attenteRH) {
                          statut = 'En attente RH';
                          statutBadge = 'gss-badge gss-badge-blue';
                          barColor = 'var(--gss-info)';
                        } else if (attenteSup) {
                          statut = 'En attente superviseur';
                          statutBadge = 'gss-badge gss-badge-orange';
                          barColor = 'var(--gss-warning)';
                        } else {
                          statut = 'Non commence';
                          statutBadge = 'gss-badge gss-badge-grey';
                          barColor = 'var(--gss-text-light)';
                        }

                        return (
                          <tr key={a.id}>
                            <td>
                              {a.photo_url ? (
                                <ImageProtegee src={urlDocument('photo-agent', a.id)} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                              ) : (
                                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--gss-text-light)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700 }}>
                                  {a.prenom?.[0]}{a.nom?.[0]}
                                </div>
                              )}
                            </td>
                            <td><strong>{a.matricule || '—'}</strong></td>
                            <td>{a.prenom} {a.nom}</td>
                            <td>
                              <span className={`gss-badge ${a.type_personne === 'agent' ? 'gss-badge-blue' : 'gss-badge-orange'}`}>
                                {a.type_personne === 'agent' ? 'Agent' : 'Controleur'}
                              </span>
                            </td>
                            <td style={{ minWidth: 180 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <div style={{ flex: 1, height: 6, background: 'var(--gss-border, #e0e0e0)', borderRadius: 3, overflow: 'hidden' }}>
                                  <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: 3, transition: 'width 0.3s ease' }} />
                                </div>
                                <span style={{ fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{a.modules_valides}/{a.total_modules}</span>
                              </div>
                            </td>
                            <td>
                              <span className={statutBadge}>
                                {statut}
                              </span>
                            </td>
                            <td>
                              {complet && (
                                <button className="gss-btn-sm gss-btn-success"
                                  onClick={() => handleGenererAttestation(a.id)}
                                  disabled={generating === a.id}>
                                  {generating === a.id ? '...' : 'Attestation PDF'}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── PARCOURS ── */}
          {tab === 'parcours' && (
            <div className="content-card">
              <div className="gss-toolbar">
                <div className="content-card-title" style={{ marginBottom: 0 }}>Parcours de formation</div>
                <button className="gss-btn gss-btn-primary" onClick={() => { setParcoursForm({ code_fonction: '', titre: '', description: '' }); setEditParcoursId(null); setShowParcoursForm(true); }}>
                  + Nouveau parcours
                </button>
              </div>
              {parcours.length === 0 ? (
                <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Aucun parcours</p>
              ) : (
                <div className="gss-table-wrap">
                  <table className="gss-table">
                    <thead>
                      <tr><th>Fonction</th><th>Titre</th><th>Description</th><th>Modules</th><th>Actif</th><th style={{ width: 60 }}>Actions</th></tr>
                    </thead>
                    <tbody>
                      {parcours.map(p => (
                        <tr key={p.id}>
                          <td><span className="gss-badge" style={{ background: '#e3f2fd', color: '#1565c0' }}>{p.code_fonction}</span></td>
                          <td><strong>{p.titre}</strong></td>
                          <td style={{ fontSize: '0.8rem', color: 'var(--gss-text-light)', maxWidth: 300 }}>{p.description ? p.description.substring(0, 100) : '-'}</td>
                          <td style={{ textAlign: 'center', fontWeight: 600 }}>{p.nb_modules}</td>
                          <td>{p.actif ? <span className="gss-badge gss-badge-green">Actif</span> : <span className="gss-badge gss-badge-grey">Inactif</span>}</td>
                          <td>
                            <button className="gss-btn-icon" onClick={() => handleEditParcours(p)} title="Modifier">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── MODULES ── */}
          {tab === 'modules' && (
            <div className="content-card">
              <div className="gss-toolbar">
                <div className="content-card-title" style={{ marginBottom: 0 }}>Modules de formation</div>
                <button className="gss-btn gss-btn-primary" onClick={() => { setModuleForm(EMPTY_MODULE); setEditModuleId(null); setShowModuleForm(true); }}>
                  + Nouveau module
                </button>
              </div>
              <div className="gss-table-wrap">
                <table className="gss-table">
                  <thead>
                    <tr><th>Ordre</th><th>Titre</th><th>Parcours</th><th>Type</th><th>Duree</th><th>Obligatoire</th><th>Actif</th><th style={{ width: 90 }}>Actions</th></tr>
                  </thead>
                  <tbody>
                    {modules.map(mod => (
                      <tr key={mod.id}>
                        <td>{mod.ordre}</td>
                        <td>
                          <strong>{mod.titre}</strong>
                          {mod.description && <div style={{ fontSize: '0.75rem', color: 'var(--gss-text-light)' }}>{mod.description.substring(0, 80)}</div>}
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>{mod.parcours_titre || <span style={{ color: '#bbb' }}>—</span>}</td>
                        <td><span className={`gss-badge ${mod.type === 'theorique' ? 'gss-badge-blue' : mod.type === 'pratique' ? 'gss-badge-orange' : 'gss-badge-grey'}`}>{mod.type}</span></td>
                        <td>{mod.duree_estimee || '-'}</td>
                        <td>{mod.obligatoire ? 'Oui' : 'Non'}</td>
                        <td>{mod.actif ? <span className="gss-badge gss-badge-green">Actif</span> : <span className="gss-badge gss-badge-grey">Inactif</span>}</td>
                        <td>
                          <button className="gss-btn-icon" onClick={() => handleEditModule(mod)} title="Modifier">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                          {mod.document_url && (
                            <button className="gss-btn-sm" onClick={() => apercuDocument(urlDocument('module-formation', mod.id))}>PDF</button>
                          )}
                          {mod.document_url && (
                            <BoutonTelechargerDocument url={urlDocument('module-formation', mod.id)} format="PDF" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── ATTESTATIONS ── */}
          {tab === 'attestations' && (
            <div className="content-card">
              <div className="content-card-title">Attestations delivrees</div>
              {attestations.length === 0 ? (
                <p style={{ color: 'var(--gss-text-light)', fontSize: '0.85rem' }}>Aucune attestation</p>
              ) : (
                <div className="gss-table-wrap">
                  <table className="gss-table">
                    <thead>
                      <tr><th>Numero</th><th>Personne</th><th>Matricule</th><th>Parcours</th><th>Date</th><th>Delivree par</th><th>Document</th></tr>
                    </thead>
                    <tbody>
                      {attestations.map(att => (
                        <tr key={att.id}>
                          <td><strong>{att.numero}</strong></td>
                          <td>{att.prenom} {att.nom}</td>
                          <td>{att.matricule}</td>
                          <td>{att.parcours_titre || '—'}</td>
                          <td>{att.date_delivrance?.split('T')[0]}</td>
                          <td>{att.delivree_par_prenom ? `${att.delivree_par_prenom} ${att.delivree_par_nom}` : '—'}</td>
                          <td>
                            {/* ⚠️ Ce bouton s'intitulait « Telecharger PDF » et faisait un APERCU :
                                le libelle mentait avant ce lot. Il garde son comportement (onglet)
                                et prend le libelle des autres ecrans ; le telechargement reel est
                                le bouton partage voisin. */}
                            <button className="gss-btn-sm gss-btn-success" onClick={() => apercuDocument(urlDocument('attestation', att.id))}>
                              PDF
                            </button>
                            <BoutonTelechargerDocument url={urlDocument('attestation', att.id)} format="PDF" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── FORMULAIRE PARCOURS ── */}
      {showParcoursForm && (
        <div className="gss-modal-overlay" onClick={() => setShowParcoursForm(false)}>
          <div className="gss-modal" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></svg>
              <div>
                <h2>{editParcoursId ? 'Modifier le parcours' : 'Nouveau parcours'}</h2>
                <div className="gss-modal-subtitle">Parcours de formation par fonction</div>
              </div>
              <button className="gss-modal-close" onClick={() => setShowParcoursForm(false)}>&times;</button>
            </div>
            <form onSubmit={handleParcoursSubmit}>
              <div style={{ padding: '1.5rem' }}>
                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label>Code fonction *</label>
                    <input className="gss-input" required value={parcoursForm.code_fonction} onChange={e => setParcoursForm(f => ({ ...f, code_fonction: e.target.value }))}
                      placeholder="Ex: agent_securite, controleur" disabled={!!editParcoursId} />
                  </div>
                  <div className="gss-field">
                    <label>Titre *</label>
                    <input className="gss-input" required value={parcoursForm.titre} onChange={e => setParcoursForm(f => ({ ...f, titre: e.target.value }))} />
                  </div>
                  <div className="gss-field gss-field-full">
                    <label>Description</label>
                    <textarea className="gss-input" rows="3" value={parcoursForm.description} onChange={e => setParcoursForm(f => ({ ...f, description: e.target.value }))} style={{ resize: 'vertical' }} />
                  </div>
                </div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowParcoursForm(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>{saving ? 'Enregistrement...' : 'Enregistrer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── FORMULAIRE MODULE ── */}
      {showModuleForm && (
        <div className="gss-modal-overlay" onClick={() => setShowModuleForm(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>
              <div>
                <h2>{editModuleId ? 'Modifier le module' : 'Nouveau module'}</h2>
                <div className="gss-modal-subtitle">Configuration du module de formation</div>
              </div>
              <button className="gss-modal-close" onClick={() => setShowModuleForm(false)}>&times;</button>
            </div>
            <form onSubmit={handleModuleSubmit}>
              <div style={{ padding: '1.5rem' }}>
                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/>
                  </svg>
                  <span>INFORMATIONS GENERALES</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label>Parcours *</label>
                    <select className="gss-input" required value={moduleForm.parcours_id} onChange={e => setMField('parcours_id', e.target.value)}>
                      <option value="">-- Choisir un parcours --</option>
                      {parcours.filter(p => p.actif).map(p => (
                        <option key={p.id} value={p.id}>{p.titre}</option>
                      ))}
                    </select>
                  </div>
                  <div className="gss-field">
                    <label>Titre *</label>
                    <input className="gss-input" required value={moduleForm.titre} onChange={e => setMField('titre', e.target.value)} />
                  </div>
                  <div className="gss-field gss-field-full">
                    <label>Description</label>
                    <textarea className="gss-input" rows="2" value={moduleForm.description} onChange={e => setMField('description', e.target.value)} style={{ resize: 'vertical' }} />
                  </div>
                  <div className="gss-field gss-field-full">
                    <label>Contenu pedagogique</label>
                    <textarea className="gss-input" rows="4" value={moduleForm.contenu} onChange={e => setMField('contenu', e.target.value)} style={{ resize: 'vertical' }} placeholder="Contenu detaille du module..." />
                  </div>
                </div>

                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
                  </svg>
                  <span>PARAMETRES DU MODULE</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label>Type</label>
                    <select className="gss-input" value={moduleForm.type} onChange={e => setMField('type', e.target.value)}>
                      <option value="theorique">Theorique</option><option value="pratique">Pratique</option><option value="autre">Autre</option>
                    </select>
                  </div>
                  <div className="gss-field">
                    <label>Duree estimee</label>
                    <input className="gss-input" value={moduleForm.duree_estimee} onChange={e => setMField('duree_estimee', e.target.value)} placeholder="Ex: 2 heures, 1 journee" />
                  </div>
                  <div className="gss-field">
                    <label>Ordre</label>
                    <input className="gss-input" type="number" value={moduleForm.ordre} onChange={e => setMField('ordre', parseInt(e.target.value) || 0)} />
                  </div>
                  <div className="gss-field" style={{ display: 'flex', alignItems: 'end', gap: '0.5rem', paddingBottom: '0.55rem' }}>
                    <input type="checkbox" checked={moduleForm.obligatoire} onChange={e => setMField('obligatoire', e.target.checked)} id="mod_oblig" />
                    <label htmlFor="mod_oblig" style={{ fontSize: '0.85rem', marginBottom: 0 }}>Module obligatoire</label>
                  </div>
                </div>

                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                  <span>DOCUMENT</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field gss-field-full">
                    <label>Document PDF</label>
                    <input type="file" accept=".pdf" onChange={e => setDocFile(e.target.files[0])} style={{ fontSize: '0.85rem' }} />
                  </div>
                </div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowModuleForm(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>{saving ? 'Enregistrement...' : 'Enregistrer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
